// drawOla multi-file storage — scene thumbnails (Studio Phase 2).
//
// Fork-only. No upstream counterpart, so it never conflicts on merge.
//
// A thumbnail is a small light-background raster of the scene, kept on
// `DocMeta.thumbnail` as a data URL so the Studio grid can render a real
// preview without loading (and re-rendering) every doc.
//
// Two producers:
//   1. `useMultiFileStore` — regenerates the ACTIVE doc's preview as the user
//      edits, throttled to `THUMBNAIL_MIN_INTERVAL_MS`, plus unconditionally
//      on `flushSave()` (switching docs / returning to the Studio).
//   2. `startThumbnailBackfill` — fills in draws created before Phase 2, one
//      at a time at idle priority, while the Studio is on screen.
//
// Both paths are best-effort: a failed thumbnail is swallowed, never surfaced,
// and never turns into a failed save.
//
// SIZE LEDGER: thumbnails are deliberately EXCLUDED from `DocMeta.sizeBytes`.
// `estimateDocBytes()` (scene JSON + binary data URLs) is what the pre-flight
// `canWrite()` check in `useMultiFileStore` computes from the in-memory scene,
// so folding the preview into the stored `sizeBytes` would put the ledger and
// the pre-flight check permanently out of step and could push a doc sitting
// near `MAX_BYTES_PER_DOC` over the edge for a reason the user cannot see or
// act on. It also keeps the Studio's per-row size column reporting the
// drawing, not our cache. The real IndexedDB cost is bounded instead:
// `THUMBNAIL_SOFT_MAX_BYTES` per doc × `LIMITS.MAX_DOCUMENTS` ≈ 2.4 MB
// worst case across the whole store.

import { exportToCanvas } from "@excalidraw/excalidraw";

import type {
  ExcalidrawElement,
  NonDeleted,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import { getDoc, listDocs, setDocThumbnail } from "./multiFileStore";

/** Longest edge of the generated preview, in CSS px. */
export const THUMBNAIL_MAX_EDGE = 320;

/** Floor between two thumbnails of the active doc during continuous editing. */
export const THUMBNAIL_MIN_INTERVAL_MS = 10_000;

/** Above this we re-encode harder; a soft target, not a hard reject. */
const THUMBNAIL_SOFT_MAX_BYTES = 24 * 1024;

const THUMBNAIL_PADDING = 8;
const WEBP_QUALITY = 0.72;
const WEBP_QUALITY_RETRY = 0.45;

/** Cap on how long the browser may defer a backfill step. */
const IDLE_TIMEOUT_MS = 2_000;
/** `requestIdleCallback` fallback delay — long enough to stay out of the way. */
const IDLE_FALLBACK_MS = 300;

/**
 * Encode a rendered canvas as compactly as the browser allows.
 *
 * WebP is ~3-5× smaller than PNG for these, but Safari < 14 and some embedded
 * webviews silently hand back a PNG data URL from `toDataURL("image/webp")`
 * instead of failing — hence the prefix sniff rather than a feature test.
 */
const encodeCanvas = (canvas: HTMLCanvasElement): string | null => {
  let webp: string | null = null;
  try {
    webp = canvas.toDataURL("image/webp", WEBP_QUALITY);
  } catch {
    webp = null;
  }
  if (webp && webp.startsWith("data:image/webp")) {
    if (webp.length > THUMBNAIL_SOFT_MAX_BYTES) {
      try {
        const smaller = canvas.toDataURL("image/webp", WEBP_QUALITY_RETRY);
        if (
          smaller.startsWith("data:image/webp") &&
          smaller.length < webp.length
        ) {
          return smaller;
        }
      } catch {
        // keep the first encode
      }
    }
    return webp;
  }
  try {
    const png = canvas.toDataURL("image/png");
    return png.startsWith("data:image/") ? png : null;
  } catch {
    return null;
  }
};

/**
 * Render a scene to a small preview data URL.
 *
 * @returns `null` for an empty scene (the Studio then shows its placeholder)
 *   or when the browser cannot encode the canvas.
 */
export const generateThumbnail = async (
  elements: readonly OrderedExcalidrawElement[],
  appState: Partial<AppState> | undefined,
  files: BinaryFiles | undefined,
): Promise<string | null> => {
  const visible = elements.filter(
    (element) => !element.isDeleted,
  ) as readonly NonDeleted<ExcalidrawElement>[];

  if (!visible.length) {
    return null;
  }

  const canvas = await exportToCanvas({
    elements: visible,
    // Cast so the object is not a *fresh* literal: `AppState` carries
    // `offsetTop`/`offsetLeft`, which the export opts type omits.
    appState: {
      ...appState,
      // previews are always light and always framed — the Studio grid is a
      // light surface even when the editor is in dark mode
      exportBackground: true,
      exportWithDarkMode: false,
      exportEmbedScene: false,
      exportScale: 1,
      viewBackgroundColor: appState?.viewBackgroundColor || "#ffffff",
    } as Partial<AppState>,
    files: files ?? {},
    maxWidthOrHeight: THUMBNAIL_MAX_EDGE,
    exportPadding: THUMBNAIL_PADDING,
  });

  if (!canvas.width || !canvas.height) {
    return null;
  }

  return encodeCanvas(canvas);
};

/**
 * Generate and persist a doc's preview. Never throws.
 *
 * @returns `true` when the stored thumbnail actually changed, so callers can
 *   decide whether a list refresh is worth it.
 */
export const refreshDocThumbnail = async (
  docId: string,
  elements: readonly OrderedExcalidrawElement[],
  appState: Partial<AppState> | undefined,
  files: BinaryFiles | undefined,
): Promise<boolean> => {
  try {
    const thumbnail = await generateThumbnail(elements, appState, files);
    return await setDocThumbnail(docId, thumbnail);
  } catch (error) {
    // A preview is a nicety: swallow font-loading, canvas-taint, encoder and
    // storage failures alike rather than letting them reach a save path.
    // eslint-disable-next-line no-console
    console.warn("[drawOla] thumbnail generation failed", error);
    return false;
  }
};

export type ThumbnailBackfill = {
  /** Stop after the in-flight doc; safe to call more than once. */
  cancel: () => void;
};

/**
 * Fill in missing previews for docs created before Phase 2.
 *
 * Strictly background work: one doc per idle slice, no retries for docs that
 * fail, and fully abortable — the Studio cancels it on unmount, which is also
 * the moment the app switches back to the editor.
 */
export const startThumbnailBackfill = (opts?: {
  /** Fired after each doc whose stored thumbnail changed. */
  onDocUpdated?: (docId: string) => void;
}): ThumbnailBackfill => {
  let cancelled = false;
  let idleId: number | null = null;
  let timeoutId: number | null = null;

  const schedule = (step: () => void) => {
    if (cancelled) {
      return;
    }
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(
        () => {
          idleId = null;
          step();
        },
        { timeout: IDLE_TIMEOUT_MS },
      );
    } else {
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        step();
      }, IDLE_FALLBACK_MS);
    }
  };

  const run = async () => {
    let queue: string[] = [];
    try {
      queue = (await listDocs())
        .filter((doc) => !doc.thumbnail)
        .map((doc) => doc.id);
    } catch {
      return;
    }

    const step = async () => {
      if (cancelled) {
        return;
      }
      const docId = queue.shift();
      if (docId === undefined) {
        return;
      }
      try {
        const doc = await getDoc(docId);
        if (!cancelled && doc) {
          const changed = await refreshDocThumbnail(
            docId,
            doc.elements,
            doc.appState,
            doc.files,
          );
          if (changed && !cancelled) {
            opts?.onDocUpdated?.(docId);
          }
        }
      } catch {
        // Skip this doc for good — it is already off the queue, so a doc that
        // cannot be rendered never turns into a retry loop.
      }
      schedule(() => {
        void step();
      });
    };

    schedule(() => {
      void step();
    });
  };

  schedule(() => {
    void run();
  });

  return {
    cancel: () => {
      cancelled = true;
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      idleId = null;
      timeoutId = null;
    },
  };
};
